import { useState } from "react";
import styles from "./CommonFields.module.css";
import {
    Button,
    ButtonGroup,
    Checkbox,
    Collapsible,
    Divider,
    FormLayout,
    Icon,
    Label,
    TextField,
    Tooltip,
} from "@shopify/polaris";
import { MdOutlineArrowBackIosNew } from "react-icons/md";

import useBundleMixpanelHook from "../../../hooks/useBundleMixpanelHook";
import { InfoIcon } from "@shopify/polaris-icons";
import { BundleTypes } from "../constants";
import { Field } from "@shopify/react-form";
import MantleDoc, { mantleDocHandles } from "../../../components/MantleDoc";

type Props_TextFields = {
    dispatchBundleConfig: any;
    add_to_cart_cta_text?: any;
    add_to_cart_cta_hover_text?: Field<string>;
    default_cta?: any;
    bundleType?: string;
    quantity_text?: any;
    footer_text?: any;
    footer_total_text?: any;
    add_button?: any;
    offer_free_gift_text?: Field<string>;
    option_items_text?: any;
    option_off_text?: any;
    option_upto_text?: any;
    y_label_get_text?: any;
    y_label_off_text?: any;
    free_text?: any;
    item_added_text?: Field<string>;
    x_label_buy_text?: any;
    skip_offer_cta_text?: any;
    buy_now_cta_text?: any;
    edit_cta_text?: any;
    start_again_cta_text?: any;
    next_cta_text?: any;
    done_cta_text?: any;
    previous_cta_text?: any;
    checkout_cta_text?: any;
    checkout_continue_shopping_cta_text?: Field<string>;
    checkout_view_cart_cta_text?: Field<string>;
    show_more_text?: Field<string>;
    show_less_text?: Field<string>;
    out_of_stock_text?: any;
    order_summary_title?: any;
    order_summary_subtotal?: any;
    order_summary_discount?: any;
    bundle_ready_text?: any;
    show_unit_price_text?: any;
    out_of_stock_disable_text?: any;
    secondary_button_type?: any;
    no_stock_text?: Field<string>;
    main_product_out_of_stock_text?: Field<string>;
    empty_card_footer_text?: Field<string>;
    load_more_products_text?: Field<string>;
    choose_option_text?: Field<string>;
};

const TextFields = ({
    dispatchBundleConfig,
    bundleType,
    add_to_cart_cta_text,
    add_to_cart_cta_hover_text,
    default_cta,
    quantity_text,
    footer_text,
    footer_total_text,
    option_items_text,
    option_off_text,
    option_upto_text,
    add_button,
    offer_free_gift_text,
    y_label_get_text,
    free_text,
    item_added_text,
    y_label_off_text,
    x_label_buy_text,
    skip_offer_cta_text,
    buy_now_cta_text,
    out_of_stock_text,
    edit_cta_text,
    start_again_cta_text,
    next_cta_text,
    done_cta_text,
    previous_cta_text,
    checkout_cta_text,
    checkout_continue_shopping_cta_text,
    checkout_view_cart_cta_text,
    show_more_text,
    show_less_text,
    order_summary_title,
    order_summary_subtotal,
    order_summary_discount,
    bundle_ready_text,
    show_unit_price_text,
    out_of_stock_disable_text,
    secondary_button_type = "edit_bundle",
    no_stock_text,
    main_product_out_of_stock_text,
    empty_card_footer_text,
    load_more_products_text,
    choose_option_text,
}: Props_TextFields) => {
    const [expandTextFields, setExpandTextFields] = useState(false);
    const { trackFormSections } = useBundleMixpanelHook();

    const appendToCTA = (text: string) => {
        const current = add_to_cart_cta_text.value || "";
        const newValue = current + " " + text;

        if (newValue.length > 50) return;

        dispatchBundleConfig({
            payload: newValue,
            key: "add_to_cart_cta_text",
        });
        add_to_cart_cta_text.onChange(newValue);
    };

    return (
        <>
            <div
                className={styles.sectionTitleWrapper}
                onClick={() =>
                    setExpandTextFields((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Text Fields ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: bundleType,
                        });
                        return !ps;
                    })
                }
            >
                <MantleDoc handle={mantleDocHandles[bundleType!].changeBundleText} title="Text Fields" />

                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transform: expandTextFields ? "rotate(90deg)" : "rotate(270deg)",
                    }}
                >
                    <MdOutlineArrowBackIosNew size={14} />
                </div>
            </div>
            <Collapsible
                open={expandTextFields}
                id="basic-collapsible"
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
            >
                <FormLayout>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "5px",
                        }}
                    >
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                            }}
                        >
                            {show_unit_price_text && (
                                <>
                                    <div className={styles.customCardLabelWrapper}>
                                        <Label id="">
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "3px",
                                                }}
                                            >
                                                Show unit price text
                                                <div>
                                                    <Tooltip
                                                        width="wide"
                                                        content={
                                                            "This will be shown if you have selected 'Show unit prices' in the Advanced tab"
                                                        }
                                                    >
                                                        <Icon source={InfoIcon} tone="subdued" />
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        </Label>
                                    </div>
                                    <TextField
                                        placeholder="text"
                                        autoComplete="off"
                                        maxLength={20}
                                        {...show_unit_price_text}
                                        onBlur={show_unit_price_text?.onBlur}
                                        showCharacterCount
                                        onChange={(val) => {
                                            dispatchBundleConfig({
                                                payload: val,
                                                key: "show_unit_price_text",
                                            });
                                            show_unit_price_text.onChange(val);
                                        }}
                                    />
                                    <div className={styles.divider}>
                                        <Divider />
                                    </div>
                                </>
                            )}

                            {out_of_stock_disable_text && (
                                <>
                                    <div className={styles.customCardLabelWrapper}>
                                        <Label id="">
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "3px",
                                                }}
                                            >
                                                Out of stock Message
                                                <div>
                                                    <Tooltip
                                                        width="wide"
                                                        content={
                                                            "This is shown as a tooltip message when products are out of stock"
                                                        }
                                                    >
                                                        <Icon source={InfoIcon} tone="subdued" />
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        </Label>
                                    </div>
                                    <TextField
                                        placeholder="text"
                                        autoComplete="off"
                                        maxLength={80}
                                        {...out_of_stock_disable_text}
                                        onBlur={out_of_stock_disable_text?.onBlur}
                                        showCharacterCount
                                        onChange={(val) => {
                                            dispatchBundleConfig({
                                                payload: val,
                                                key: "out_of_stock_disable_text",
                                            });
                                            out_of_stock_disable_text.onChange(val);
                                        }}
                                        helpText={`Use {itemCount} to show available quantity`}
                                    />
                                    <div className={styles.divider}>
                                        <Divider />
                                    </div>
                                </>
                            )}

                            {bundleType !== BundleTypes.PRODUCT_ADDONS &&
                                (option_items_text || option_off_text || option_upto_text || offer_free_gift_text) && (
                                    <div>
                                        <div className={styles.customCardLabelWrapper}>
                                            <Label id="">Options Text</Label>
                                        </div>
                                        <div className={`${styles.formRow} ${styles.row4}`}>
                                            {option_items_text && (
                                                <TextField
                                                    label={"Items"}
                                                    placeholder="Items"
                                                    autoComplete="off"
                                                    maxLength={10}
                                                    {...option_items_text}
                                                    onBlur={option_items_text?.onBlur}
                                                    showCharacterCount
                                                    onChange={(val) => {
                                                        dispatchBundleConfig({
                                                            payload: val,
                                                            key: "option_items_text",
                                                        });
                                                        option_items_text.onChange(val);
                                                    }}
                                                />
                                            )}

                                            {option_off_text && (
                                                <TextField
                                                    label={"Off"}
                                                    placeholder="Off"
                                                    autoComplete="off"
                                                    maxLength={10}
                                                    {...option_off_text}
                                                    onBlur={option_off_text?.onBlur}
                                                    showCharacterCount
                                                    onChange={(val) => {
                                                        dispatchBundleConfig({
                                                            payload: val,
                                                            key: "option_off_text",
                                                        });
                                                        option_off_text.onChange(val);
                                                    }}
                                                />
                                            )}

                                            {option_upto_text && (
                                                <TextField
                                                    label={"Upto"}
                                                    placeholder="Upto"
                                                    autoComplete="off"
                                                    maxLength={10}
                                                    {...option_upto_text}
                                                    onBlur={option_upto_text?.onBlur}
                                                    showCharacterCount
                                                    onChange={(val) => {
                                                        dispatchBundleConfig({
                                                            payload: val,
                                                            key: "option_upto_text",
                                                        });
                                                        option_upto_text.onChange(val);
                                                    }}
                                                />
                                            )}

                                            {offer_free_gift_text && (
                                                <TextField
                                                    label={"Offer Free Gift"}
                                                    placeholder="+ Free Gift"
                                                    autoComplete="off"
                                                    maxLength={20}
                                                    {...offer_free_gift_text}
                                                    onBlur={offer_free_gift_text?.onBlur}
                                                    showCharacterCount
                                                    onChange={(val) => {
                                                        dispatchBundleConfig({
                                                            payload: val,
                                                            key: "offer_free_gift_text",
                                                        });
                                                        offer_free_gift_text.onChange(val);
                                                    }}
                                                />
                                            )}
                                        </div>
                                        <div className={styles.divider}>
                                            <Divider />
                                        </div>
                                    </div>
                                )}

                            {y_label_get_text && y_label_off_text && (
                                <div>
                                    <div className={styles.customCardLabelWrapper}>
                                        <Label id="">Y Label</Label>
                                    </div>
                                    <div className={`${styles.formRow}`}>
                                        {y_label_get_text && (
                                            <TextField
                                                label={"GET"}
                                                placeholder="GET"
                                                autoComplete="off"
                                                maxLength={20}
                                                {...y_label_get_text}
                                                onBlur={y_label_get_text?.onBlur}
                                                showCharacterCount
                                                onChange={(val) => {
                                                    dispatchBundleConfig({
                                                        payload: val,
                                                        key: "y_label_get_text",
                                                    });
                                                    y_label_get_text.onChange(val);
                                                }}
                                            />
                                        )}

                                        {y_label_off_text && (
                                            <TextField
                                                label={"Off"}
                                                placeholder="Off"
                                                autoComplete="off"
                                                maxLength={10}
                                                {...y_label_off_text}
                                                onBlur={y_label_off_text?.onBlur}
                                                showCharacterCount
                                                onChange={(val) => {
                                                    dispatchBundleConfig({
                                                        payload: val,
                                                        key: "y_label_off_text",
                                                    });
                                                    y_label_off_text.onChange(val);
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className={styles.divider}>
                                        <Divider />
                                    </div>
                                </div>
                            )}

                            {buy_now_cta_text && (
                                <div>
                                    <div className={styles.customCardLabelWrapper} style={{ marginBottom: "5px" }}>
                                        <Label id="">Product Card</Label>
                                    </div>

                                    <div className={bundleType === "bxgy" ? "" : styles.formRow}>
                                        {buy_now_cta_text && (
                                            <TextField
                                                label={"Product Card CTA"}
                                                placeholder="Buy Now"
                                                autoComplete="off"
                                                maxLength={20}
                                                {...buy_now_cta_text}
                                                onBlur={buy_now_cta_text?.onBlur}
                                                showCharacterCount
                                                onChange={(val) => {
                                                    dispatchBundleConfig({
                                                        payload: val,
                                                        key: "buy_now_cta_text",
                                                    });
                                                    buy_now_cta_text.onChange(val);
                                                }}
                                            />
                                        )}
                                        {out_of_stock_text && (
                                            <TextField
                                                label={"Out of stock"}
                                                placeholder={"Out of stock"}
                                                autoComplete={"off"}
                                                maxLength={20}
                                                {...out_of_stock_text}
                                                onBlur={out_of_stock_text?.onBlur}
                                                showCharacterCount
                                                onChange={(val) => {
                                                    dispatchBundleConfig({
                                                        payload: val,
                                                        key: "out_of_stock_text",
                                                    });
                                                    out_of_stock_text.onChange(val);
                                                }}
                                            />
                                        )}
                                        {choose_option_text && (
                                            <TextField
                                                label={"Choose Option Text"}
                                                placeholder={"Choose Option"}
                                                autoComplete={"off"}
                                                maxLength={30}
                                                {...choose_option_text}
                                                onBlur={choose_option_text?.onBlur}
                                                showCharacterCount
                                                onChange={(val) => {
                                                    dispatchBundleConfig({
                                                        payload: val,
                                                        key: "choose_option_text",
                                                    });
                                                    choose_option_text.onChange(val);
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className={styles.divider}>
                                        <Divider />
                                    </div>
                                </div>
                            )}

                            {(show_more_text || show_less_text) && (
                                <div>
                                    <div className={styles.customCardLabelWrapper} style={{ marginBottom: "5px" }}>
                                        <Label id="">Product Card Info Modal</Label>
                                    </div>

                                    <div className={styles.formRow}>
                                        {show_more_text && (
                                            <TextField
                                                label={"Show More Text"}
                                                placeholder={"Show more"}
                                                autoComplete={"off"}
                                                maxLength={60}
                                                {...show_more_text}
                                                onBlur={show_more_text?.onBlur}
                                                showCharacterCount
                                                onChange={(val) => {
                                                    dispatchBundleConfig({
                                                        payload: val,
                                                        key: "show_more_text",
                                                    });
                                                    show_more_text.onChange(val);
                                                }}
                                            />
                                        )}
                                        {show_less_text && (
                                            <TextField
                                                label={"Show Less Text"}
                                                placeholder={"Show less"}
                                                autoComplete={"off"}
                                                maxLength={60}
                                                {...show_less_text}
                                                onBlur={show_less_text?.onBlur}
                                                showCharacterCount
                                                onChange={(val) => {
                                                    dispatchBundleConfig({
                                                        payload: val,
                                                        key: "show_less_text",
                                                    });
                                                    show_less_text.onChange(val);
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className={styles.divider}>
                                        <Divider />
                                    </div>
                                </div>
                            )}

                            {(next_cta_text || previous_cta_text || done_cta_text) && (
                                <div>
                                    <div className={styles.customCardLabelWrapper} style={{ marginBottom: "5px" }}>
                                        <Label id="">
                                            {bundleType === BundleTypes.BUNDLE_BUILDER
                                                ? "Selected Products Bar"
                                                : "Footer"}
                                        </Label>
                                    </div>

                                    <div className={bundleType === "bxgy" ? "" : styles.formRow}>
                                        {next_cta_text && (
                                            <TextField
                                                label={"Next CTA"}
                                                placeholder="Next"
                                                autoComplete="off"
                                                maxLength={10}
                                                {...next_cta_text}
                                                onBlur={next_cta_text?.onBlur}
                                                showCharacterCount
                                                onChange={(val) => {
                                                    dispatchBundleConfig({
                                                        payload: val,
                                                        key: "next_cta_text",
                                                    });
                                                    next_cta_text.onChange(val);
                                                }}
                                            />
                                        )}
                                        {previous_cta_text && (
                                            <TextField
                                                label={"Previous CTA"}
                                                placeholder="Previous"
                                                autoComplete="off"
                                                maxLength={10}
                                                {...previous_cta_text}
                                                onBlur={previous_cta_text?.onBlur}
                                                showCharacterCount
                                                onChange={(val) => {
                                                    dispatchBundleConfig({
                                                        payload: val,
                                                        key: "previous_cta_text",
                                                    });
                                                    previous_cta_text.onChange(val);
                                                }}
                                            />
                                        )}
                                        {done_cta_text && (
                                            <TextField
                                                label={"Done CTA"}
                                                placeholder="Done"
                                                autoComplete="off"
                                                maxLength={10}
                                                {...done_cta_text}
                                                onBlur={done_cta_text?.onBlur}
                                                showCharacterCount
                                                onChange={(val) => {
                                                    dispatchBundleConfig({
                                                        payload: val,
                                                        key: "done_cta_text",
                                                    });
                                                    done_cta_text.onChange(val);
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className={styles.divider}>
                                        <Divider />
                                    </div>
                                </div>
                            )}

                            {(edit_cta_text ||
                                start_again_cta_text ||
                                checkout_cta_text ||
                                checkout_continue_shopping_cta_text ||
                                checkout_view_cart_cta_text ||
                                order_summary_title ||
                                order_summary_subtotal ||
                                order_summary_discount) && (
                                <div>
                                    <div className={styles.customCardLabelWrapper} style={{ marginBottom: "5px" }}>
                                        <Label id="">
                                            {bundleType == BundleTypes.BB_PLACEHOLDER
                                                ? "Product Placeholder"
                                                : "Review Page"}
                                        </Label>
                                    </div>

                                    <FormLayout>
                                        <div className={`${styles.formRow} `}>
                                            {edit_cta_text && (
                                                <TextField
                                                    label={
                                                        bundleType == BundleTypes.BB_PLACEHOLDER
                                                            ? "Add Product"
                                                            : "Edit Bundle CTA"
                                                    }
                                                    placeholder={
                                                        bundleType == BundleTypes.BB_PLACEHOLDER
                                                            ? "Add Product"
                                                            : "Edit Bundle"
                                                    }
                                                    autoComplete="off"
                                                    maxLength={20}
                                                    {...edit_cta_text}
                                                    onBlur={edit_cta_text?.onBlur}
                                                    showCharacterCount
                                                    disabled={secondary_button_type === "edit_bundle" ? false : true}
                                                    onChange={(val) => {
                                                        dispatchBundleConfig({
                                                            payload: val,
                                                            key: "edit_cta_text",
                                                        });
                                                        edit_cta_text.onChange(val);
                                                    }}
                                                />
                                            )}
                                            {start_again_cta_text && (
                                                <TextField
                                                    label={"Start Again CTA"}
                                                    placeholder={"Start Again"}
                                                    autoComplete="off"
                                                    maxLength={20}
                                                    {...start_again_cta_text}
                                                    onBlur={start_again_cta_text?.onBlur}
                                                    disabled={secondary_button_type === "start_again" ? false : true}
                                                    showCharacterCount
                                                    onChange={(val) => {
                                                        dispatchBundleConfig({
                                                            payload: val,
                                                            key: "start_again_cta_text",
                                                        });
                                                        start_again_cta_text.onChange(val);
                                                    }}
                                                />
                                            )}

                                            {checkout_cta_text && (
                                                <TextField
                                                    label={"Checkout CTA"}
                                                    placeholder="Checkout"
                                                    autoComplete="off"
                                                    maxLength={20}
                                                    {...checkout_cta_text}
                                                    onBlur={checkout_cta_text?.onBlur}
                                                    showCharacterCount
                                                    onChange={(val) => {
                                                        dispatchBundleConfig({
                                                            payload: val,
                                                            key: "checkout_cta_text",
                                                        });
                                                        checkout_cta_text.onChange(val);
                                                    }}
                                                />
                                            )}

                                            {bundleType == BundleTypes.BUNDLE_BUILDER && (
                                                <>
                                                    {checkout_continue_shopping_cta_text && (
                                                        <TextField
                                                            label={"Continue Shopping CTA"}
                                                            placeholder="Continue Shopping"
                                                            autoComplete="off"
                                                            maxLength={30}
                                                            {...checkout_continue_shopping_cta_text}
                                                            onBlur={checkout_continue_shopping_cta_text?.onBlur}
                                                            showCharacterCount
                                                            onChange={(val) => {
                                                                dispatchBundleConfig({
                                                                    payload: val,
                                                                    key: "checkout_continue_shopping_cta_text",
                                                                });
                                                                checkout_continue_shopping_cta_text.onChange(val);
                                                            }}
                                                        />
                                                    )}
                                                    {checkout_view_cart_cta_text && (
                                                        <TextField
                                                            label={"View Cart CTA"}
                                                            placeholder="View Cart"
                                                            autoComplete="off"
                                                            maxLength={20}
                                                            {...checkout_view_cart_cta_text}
                                                            onBlur={checkout_view_cart_cta_text?.onBlur}
                                                            showCharacterCount
                                                            onChange={(val) => {
                                                                dispatchBundleConfig({
                                                                    payload: val,
                                                                    key: "checkout_view_cart_cta_text",
                                                                });
                                                                checkout_view_cart_cta_text.onChange(val);
                                                            }}
                                                        />
                                                    )}
                                                </>
                                            )}
                                        </div>
                                        <div className={`${styles.formRow} `}>
                                            {bundle_ready_text && (
                                                <TextField
                                                    label={"Bundle Ready Title"}
                                                    placeholder="title"
                                                    autoComplete="off"
                                                    maxLength={40}
                                                    {...bundle_ready_text}
                                                    onBlur={bundle_ready_text?.onBlur}
                                                    helpText={"Use {title} for the bundle title"}
                                                    showCharacterCount
                                                    onChange={(val) => {
                                                        dispatchBundleConfig({
                                                            payload: val,
                                                            key: "bundle_ready_text",
                                                        });
                                                        bundle_ready_text.onChange(val);
                                                    }}
                                                />
                                            )}
                                            {order_summary_title && (
                                                <TextField
                                                    label={"Order Summary Title"}
                                                    placeholder="Order Summary"
                                                    autoComplete="off"
                                                    maxLength={20}
                                                    {...order_summary_title}
                                                    onBlur={order_summary_title?.onBlur}
                                                    showCharacterCount
                                                    onChange={(val) => {
                                                        dispatchBundleConfig({
                                                            payload: val,
                                                            key: "order_summary_title",
                                                        });
                                                        order_summary_title.onChange(val);
                                                    }}
                                                />
                                            )}
                                            {order_summary_subtotal && (
                                                <TextField
                                                    label={"Order Summary Subtotal"}
                                                    placeholder="subtotal"
                                                    autoComplete="off"
                                                    maxLength={20}
                                                    {...order_summary_subtotal}
                                                    onBlur={order_summary_subtotal?.onBlur}
                                                    showCharacterCount
                                                    onChange={(val) => {
                                                        dispatchBundleConfig({
                                                            payload: val,
                                                            key: "order_summary_subtotal",
                                                        });
                                                        order_summary_subtotal.onChange(val);
                                                    }}
                                                />
                                            )}
                                            {order_summary_discount && (
                                                <TextField
                                                    label={"Order Summary Discount"}
                                                    placeholder="discount"
                                                    autoComplete="off"
                                                    maxLength={20}
                                                    {...order_summary_discount}
                                                    onBlur={order_summary_discount?.onBlur}
                                                    showCharacterCount
                                                    onChange={(val) => {
                                                        dispatchBundleConfig({
                                                            payload: val,
                                                            key: "order_summary_discount",
                                                        });
                                                        order_summary_discount.onChange(val);
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </FormLayout>
                                    <div className={styles.divider}>
                                        <Divider />
                                    </div>
                                </div>
                            )}

                            {x_label_buy_text && (
                                <>
                                    <div className={styles.customCardLabelWrapper} style={{ marginBottom: "-5px" }}>
                                        <Label id="">X Label</Label>
                                    </div>
                                    <TextField
                                        // label={"Quantity Text"}
                                        placeholder="Buy"
                                        autoComplete="off"
                                        maxLength={20}
                                        showCharacterCount
                                        {...x_label_buy_text}
                                        onBlur={x_label_buy_text?.onBlur}
                                        onChange={(val) => {
                                            dispatchBundleConfig({
                                                payload: val,
                                                key: "x_label_buy_text",
                                            });
                                            x_label_buy_text.onChange(val);
                                        }}
                                    />
                                    <div className={styles.divider}>
                                        <Divider />
                                    </div>
                                </>
                            )}

                            {(footer_text || footer_total_text || item_added_text) && (
                                <div>
                                    <div className={styles.customCardLabelWrapper} style={{ marginBottom: "5px" }}>
                                        <Label id="">Footer Text</Label>
                                    </div>

                                    <div className={bundleType === "bxgy" ? "" : styles.formRow}>
                                        {footer_total_text && (
                                            <TextField
                                                label={bundleType === "bxgy" ? "" : "Total"}
                                                placeholder="Total"
                                                autoComplete="off"
                                                maxLength={10}
                                                {...footer_total_text}
                                                onBlur={footer_total_text?.onBlur}
                                                showCharacterCount
                                                onChange={(val) => {
                                                    dispatchBundleConfig({
                                                        payload: val,
                                                        key: "footer_total_text",
                                                    });
                                                    footer_total_text.onChange(val);
                                                }}
                                            />
                                        )}

                                        {footer_text && (
                                            <TextField
                                                label={"Off"}
                                                placeholder="Off"
                                                autoComplete="off"
                                                maxLength={10}
                                                {...footer_text}
                                                onBlur={footer_text?.onBlur}
                                                showCharacterCount
                                                onChange={(val) => {
                                                    dispatchBundleConfig({
                                                        payload: val,
                                                        key: "footer_text",
                                                    });
                                                    footer_text.onChange(val);
                                                }}
                                            />
                                        )}
                                        {item_added_text && (
                                            <div style={{ display: "flex", flexDirection: "column" }}>
                                                <span
                                                    style={{
                                                        display: "flex",
                                                        justifyContent: "space-between",
                                                        alignItems: "center",
                                                        width: "100%",
                                                        marginBottom: 5,
                                                    }}
                                                >
                                                    <span>Bundle Item(s) Added</span>
                                                    <Tooltip
                                                        content={"This text will be shown in the footer on mobile view"}
                                                    >
                                                        <Icon source={InfoIcon} />
                                                    </Tooltip>
                                                </span>
                                                <TextField
                                                    label={"Bundle Item(s) Added"}
                                                    labelHidden
                                                    placeholder={"{itemCount} Item(s) Added"}
                                                    autoComplete="off"
                                                    maxLength={40}
                                                    {...item_added_text}
                                                    onBlur={item_added_text?.onBlur}
                                                    showCharacterCount
                                                    onChange={(val) => {
                                                        dispatchBundleConfig({
                                                            payload: val,
                                                            key: "item_added_text",
                                                        });
                                                        item_added_text.onChange(val);
                                                    }}
                                                />
                                            </div>
                                        )}

                                        {empty_card_footer_text && (
                                            <TextField
                                                label={"Empty Card Footer Text"}
                                                placeholder="Add Product"
                                                autoComplete="off"
                                                maxLength={30}
                                                {...empty_card_footer_text}
                                                onBlur={empty_card_footer_text?.onBlur}
                                                showCharacterCount
                                                onChange={(val) => {
                                                    dispatchBundleConfig({
                                                        payload: val,
                                                        key: "empty_card_footer_text",
                                                    });
                                                    empty_card_footer_text.onChange(val);
                                                }}
                                            />
                                        )}
                                    </div>
                                    <div className={styles.divider}>
                                        <Divider />
                                    </div>
                                </div>
                            )}

                            {free_text && (
                                <>
                                    <div className={styles.customCardLabelWrapper}>
                                        <Label id="">
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "3px",
                                                }}
                                            >
                                                Free Text
                                                <div>
                                                    <Tooltip
                                                        width="wide"
                                                        content={
                                                            "This text will be shown when 100% discount is applied to the product."
                                                        }
                                                    >
                                                        <Icon source={InfoIcon} tone="subdued" />
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        </Label>
                                    </div>
                                    <TextField
                                        placeholder="Free"
                                        autoComplete="off"
                                        maxLength={35}
                                        {...free_text}
                                        onBlur={free_text?.onBlur}
                                        showCharacterCount
                                        onChange={(val) => {
                                            dispatchBundleConfig({
                                                payload: val,
                                                key: "free_text",
                                            });
                                            free_text.onChange(val);
                                        }}
                                    />
                                </>
                            )}

                            {bundleType == BundleTypes.PRODUCT_ADDONS && option_off_text && (
                                <>
                                    <div className={styles.customCardLabelWrapper}>
                                        <Label id="">Discount Off</Label>
                                    </div>
                                    <TextField
                                        label={""}
                                        placeholder={"{discount} off"}
                                        autoComplete="off"
                                        maxLength={20}
                                        {...option_off_text}
                                        onBlur={option_off_text?.onBlur}
                                        showCharacterCount
                                        onChange={(val) => {
                                            dispatchBundleConfig({
                                                payload: val,
                                                key: "option_off_text",
                                            });
                                            option_off_text.onChange(val);
                                        }}
                                    />
                                </>
                            )}

                            {quantity_text && (
                                <>
                                    <div className={styles.customCardLabelWrapper} style={{ marginBottom: "-5px" }}>
                                        <Label id="">Quantity</Label>
                                    </div>
                                    <TextField
                                        // label={"Quantity Text"}
                                        placeholder="Qty"
                                        autoComplete="off"
                                        maxLength={10}
                                        showCharacterCount
                                        {...quantity_text}
                                        onBlur={quantity_text?.onBlur}
                                        onChange={(val) => {
                                            dispatchBundleConfig({
                                                payload: val,
                                                key: "quantity_text",
                                            });
                                            quantity_text.onChange(val);
                                        }}
                                    />
                                    <div className={styles.divider}>
                                        <Divider />
                                    </div>
                                </>
                            )}

                            {load_more_products_text && (
                                <>
                                    <div className={styles.customCardLabelWrapper} style={{ marginBottom: "-5px" }}>
                                        <Label id="">Load More Products Text</Label>
                                    </div>
                                    <TextField
                                        label={"Load More Products"}
                                        labelHidden
                                        placeholder="Load More Products..."
                                        autoComplete="off"
                                        maxLength={50}
                                        showCharacterCount
                                        {...load_more_products_text}
                                        onBlur={load_more_products_text?.onBlur}
                                        onChange={(val) => {
                                            dispatchBundleConfig({
                                                payload: val,
                                                key: "load_more_products_text",
                                            });
                                            load_more_products_text.onChange(val);
                                        }}
                                    />
                                    <div className={styles.divider}>
                                        <Divider />
                                    </div>
                                </>
                            )}

                            {add_button && (
                                <>
                                    <div className={styles.customCardLabelWrapper} style={{ marginBottom: "-5px" }}>
                                        <Label id=""> Add Button</Label>
                                    </div>

                                    <TextField
                                        placeholder="Add"
                                        autoComplete="off"
                                        maxLength={20}
                                        {...add_button}
                                        onBlur={add_button?.onBlur}
                                        showCharacterCount
                                        onChange={(val) => {
                                            dispatchBundleConfig({
                                                payload: val,
                                                key: "add_button",
                                            });
                                            add_button.onChange(val);
                                        }}
                                    />
                                    <div className={styles.divider}>
                                        <Divider />
                                    </div>
                                </>
                            )}

                            {add_to_cart_cta_text && (
                                <>
                                    <div className={styles.customCardLabelWrapper} style={{ marginBottom: "-5px" }}>
                                        <Label id=""> Add to Cart CTA</Label>
                                    </div>

                                    {default_cta && bundleType !== BundleTypes.PRODUCT_ADDONS_POPUP && (
                                        <Checkbox
                                            label={
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                    }}
                                                >
                                                    Use default CTA
                                                    <div>
                                                        <Tooltip
                                                            width="wide"
                                                            content={
                                                                "You can choose to have Shopify theme's default CTA or style your own."
                                                            }
                                                        >
                                                            <Icon source={InfoIcon} tone="subdued" />
                                                        </Tooltip>
                                                    </div>
                                                </div>
                                            }
                                            value={""}
                                            checked={!!default_cta.value}
                                            onChange={(val) => {
                                                dispatchBundleConfig({
                                                    payload: {
                                                        default_cta: val,
                                                    },
                                                    isComplex: true,
                                                    key: "bundles_design",
                                                });

                                                default_cta.onChange(val);
                                            }}
                                        />
                                    )}

                                    <TextField
                                        placeholder="Add to cart CTA"
                                        autoComplete="off"
                                        maxLength={50}
                                        {...add_to_cart_cta_text}
                                        onBlur={add_to_cart_cta_text?.onBlur}
                                        showCharacterCount
                                        onChange={(val) => {
                                            dispatchBundleConfig({
                                                payload: val,
                                                key: "add_to_cart_cta_text",
                                            });
                                            add_to_cart_cta_text.onChange(val);
                                        }}
                                    />

                                    {bundleType === BundleTypes.VOLUME_DISCOUNT && (
                                        <ButtonGroup>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "0.5rem",
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                <strong>Short Codes:</strong>
                                                <Button
                                                    onClick={() => appendToCTA("{{price}}")}
                                                    disabled={add_to_cart_cta_text.value?.includes("{{price}}")}
                                                >
                                                    Price
                                                </Button>
                                            </div>
                                        </ButtonGroup>
                                    )}
                                </>
                            )}

                            {add_to_cart_cta_hover_text && (
                                <TextField
                                    label="Hover text"
                                    placeholder="Hover text"
                                    autoComplete="off"
                                    maxLength={50}
                                    {...add_to_cart_cta_hover_text}
                                    onBlur={add_to_cart_cta_hover_text?.onBlur}
                                    showCharacterCount
                                    onChange={(val) => {
                                        dispatchBundleConfig({
                                            payload: val,
                                            key: "add_to_cart_cta_hover_text",
                                        });
                                        add_to_cart_cta_hover_text.onChange(val);
                                    }}
                                />
                            )}

                            {no_stock_text && (
                                <>
                                    <div className={styles.customCardLabelWrapper} style={{ marginBottom: "-5px" }}>
                                        <Label id="">Out of Stock Text</Label>
                                    </div>
                                    <TextField
                                        label="No Stock"
                                        labelHidden
                                        placeholder="Out of stock"
                                        autoComplete="off"
                                        maxLength={40}
                                        showCharacterCount
                                        {...no_stock_text}
                                        onBlur={no_stock_text?.onBlur}
                                        onChange={(val) => {
                                            dispatchBundleConfig({
                                                payload: val,
                                                key: "no_stock_text",
                                            });
                                            no_stock_text.onChange(val);
                                        }}
                                    />
                                </>
                            )}

                            {main_product_out_of_stock_text && (
                                <TextField
                                    label={"Main Product Out of stock"}
                                    placeholder={"Main product is out of stock"}
                                    autoComplete={"off"}
                                    maxLength={50}
                                    {...main_product_out_of_stock_text}
                                    onBlur={main_product_out_of_stock_text?.onBlur}
                                    showCharacterCount
                                    onChange={(val) => {
                                        dispatchBundleConfig({
                                            payload: val,
                                            key: "main_product_out_of_stock_text",
                                        });
                                        main_product_out_of_stock_text.onChange(val);
                                    }}
                                />
                            )}

                            {skip_offer_cta_text && (
                                <>
                                    <div className={styles.customCardLabelWrapper} style={{ marginBottom: "-5px" }}>
                                        <Label id="">Skip Offer CTA</Label>
                                    </div>

                                    <TextField
                                        placeholder="Skip Offer"
                                        autoComplete="off"
                                        maxLength={50}
                                        {...skip_offer_cta_text}
                                        onBlur={skip_offer_cta_text?.onBlur}
                                        showCharacterCount
                                        onChange={(val) => {
                                            dispatchBundleConfig({
                                                payload: val,
                                                key: "skip_offer_cta_text",
                                            });
                                            skip_offer_cta_text.onChange(val);
                                        }}
                                    />
                                </>
                            )}
                        </div>
                    </div>
                </FormLayout>
            </Collapsible>
        </>
    );
};

export default TextFields;
